import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { IQueryParamsProps } from "@faire/web--source/common/query-params/IQueryParamsProps";
import { withQueryParams } from "@faire/web--source/common/query-params/withQueryParams";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useRefFrom } from "@faire/web--source/ui/hooks/useRefFrom";
import { QueryParameters as ApplyQueryParameters } from "@faire/web-api--source/routes/apply";
import { observer } from "mobx-react";
import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useSettingEnableMakerSignUpHooksMigration } from "@faire/retailer/settings/useSettingEnableMakerSignUpHooksMigration";
import { BrandsLandingPage } from "@faire/retailer/views/brands/landing-page/BrandsLandingPage";
import { LandingPage } from "@faire/retailer/views/brands/landing-page/WithOffer/LandingPage";

import { MakerSignUpAdditionalQuestions } from "./MakerApplicationAdditionalQuestions/MakerSignUpAdditionalQuestions";
import { MakerSignUpForm } from "./MakerSignUp/MakerSignUpForm";
import {
  MakerSignUpNotAccepted,
  SubmitPageStage,
} from "./MakerSignUp/MakerSignUpNotAccepted";
import { MakerApplicationStages, MakerSignUpState } from "./MakerSignUpState";
import { useMakerSignUp } from "./useMakerSignUp";
import { useMakerSignUpForm } from "./useMakerSignUpForm";

const LandingStatePage: React.FC<{
  skipToFormParam?: string | null;
  viewState: MakerSignUpState;
}> = ({ skipToFormParam, viewState }) => {
  const bonusOffersReferralNamesList = getGlobalProperty<Array<string>>(
    "referralBonusOfferReferralNames"
  );

  const bonusOffersExist =
    isNotUndefined(bonusOffersReferralNamesList) &&
    bonusOffersReferralNamesList.length > 0;

  if (bonusOffersExist && isNotUndefined(skipToFormParam)) {
    return <LandingPage />;
  }
  return <BrandsLandingPage makerApplicationState={viewState} />;
};

const BrandApplicationBase: React.FC<
  IQueryParamsProps<ApplyQueryParameters, Record<string, string | undefined>>
> = ({ queryParams }) => {
  const enableMakerSignUpHooksMigration =
    useSettingEnableMakerSignUpHooksMigration();
  const { strictLocalize } = useStrictLocalization();
  const history = useHistory();
  const viewState = useRefFrom(
    () => new MakerSignUpState(strictLocalize, history)
  ).current;

  const { applicationStage: appStage, setApplicationStage } = useMakerSignUp();

  const {
    initializeFormData: initFormData,
    setEmailAddress: setEmail,
    setBrandLeadToken,
    setTemporaryPosLinkKey,
  } = useMakerSignUpForm();

  const initializeFormData = useCallback(() => {
    if (enableMakerSignUpHooksMigration) {
      initFormData();
    } else {
      viewState.initializeFormData();
    }
  }, [enableMakerSignUpHooksMigration, initFormData, viewState]);

  const setEmailAddress = useCallback(
    (emailAddress: string) => {
      if (enableMakerSignUpHooksMigration) {
        setEmail(emailAddress);
      } else {
        viewState.setEmailAddress(emailAddress);
      }
    },
    [enableMakerSignUpHooksMigration, setEmail, viewState]
  );
  const applicationStage = useMemo(() => {
    if (enableMakerSignUpHooksMigration) {
      return appStage;
    }
    return viewState.applicationStage;
  }, [enableMakerSignUpHooksMigration, appStage, viewState.applicationStage]);

  const skipToFormParam = queryParams.get("skip_to_form");

  useEffect(() => {
    const initializeBrandLeadToken = enableMakerSignUpHooksMigration
      ? setBrandLeadToken
      : (brandLeadToken: string | null) => {
          viewState.setBrandLeadToken(brandLeadToken);
        };
    const initializeTemporaryPosLinkKey = enableMakerSignUpHooksMigration
      ? setTemporaryPosLinkKey
      : (temporaryPosLinkKey: string | undefined) => {
          viewState.setTemporaryPosLinkKey(temporaryPosLinkKey);
        };
    const initializeApplicationStage = enableMakerSignUpHooksMigration
      ? setApplicationStage
      : (applicationStage: MakerApplicationStages) => {
          viewState.setApplicationStage(applicationStage);
        };

    initializeFormData();
    viewState.initializeCountryDropdownOption();
    if (queryParams.email_address) {
      setEmailAddress(queryParams.email_address);
    }

    const brandLeadToken = queryParams.get("blt");
    const temporaryPosLinkKey = queryParams.get("temporaryPosLinkKey");

    initializeBrandLeadToken(brandLeadToken ? `bl_${brandLeadToken}` : null);
    initializeTemporaryPosLinkKey(temporaryPosLinkKey ?? undefined);
    viewState.initialize();

    if (skipToFormParam) {
      initializeApplicationStage(MakerApplicationStages.FORM);
    }
  }, [
    queryParams,
    viewState,
    skipToFormParam,
    setEmailAddress,
    setBrandLeadToken,
    setTemporaryPosLinkKey,
    setApplicationStage,
    enableMakerSignUpHooksMigration,
    initializeFormData,
  ]);

  switch (applicationStage) {
    case MakerApplicationStages.LANDING:
      return (
        <LandingStatePage
          skipToFormParam={skipToFormParam}
          viewState={viewState}
        />
      );
    case MakerApplicationStages.FORM:
      return <MakerSignUpForm viewState={viewState} />;
    case MakerApplicationStages.ADDITIONAL_QUESTIONS:
      return (
        <MakerSignUpAdditionalQuestions
          brandLeadToken={viewState.brandLeadToken ?? ""}
        />
      );
    case MakerApplicationStages.WAITLIST:
      return (
        <MakerSignUpNotAccepted
          onClickTellUsMore={viewState.onClickTellUsMoreFunction}
          currentPage={SubmitPageStage.WAITLISTED}
        />
      );
    case MakerApplicationStages.UNSUPPORTED_AREA:
      return (
        <MakerSignUpNotAccepted
          currentPage={SubmitPageStage.UNSUPPORTED_AREA}
        />
      );
    default:
      return null;
  }
};

export const BrandApplication = withQueryParams(observer(BrandApplicationBase));
